import { useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { Library } from "@/api/admin-api"

import { Pencil } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Spinner } from "@/components/ui/spinner"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"

import { FilthSearchSettings } from "./filth-search-settings"
import { LibraryEditorForm } from "./library-editor/library-editor-form"
import {
  EMPTY_EDITOR_VALUES,
  type LibraryEditorValues,
  libraryToEditorValues,
} from "./library-editor/library-editor-model"
import { useLibraryEditor } from "./library-editor/use-library-editor"
import { RawJsonTab } from "./shared/raw-json-tab"
import { useRawEntityQuery, useSaveRawEntityMutation } from "../hooks/admin-queries"

type EditLibraryDialogProps = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (libraryId: string, values: LibraryEditorValues) => Promise<void>
  isPending: boolean
  library: Library | null
  embeddingModels?: Array<{ id: string; displayName: string }>
  /** All libraries (for copy-auth); SharePoint-family entries with auth are offered, minus this one. */
  existingLibraries?: Library[]
}

export function EditLibraryDialog({
  isOpen,
  onOpenChange,
  onSubmit,
  isPending,
  library,
  embeddingModels = [],
  existingLibraries = [],
}: EditLibraryDialogProps) {
  const { t } = useTranslation()

  const initialValues = useMemo(
    () => (library ? libraryToEditorValues(library) : EMPTY_EDITOR_VALUES),
    [library]
  )
  const editor = useLibraryEditor({ mode: "edit", initialValues, library, existingLibraries })

  // Raw JSON tab plumbing. The endpoint is nullable until we have a library
  // (the dialog is mounted with library=null initially). The query is enabled
  // only while the dialog is open.
  const rawEndpoint = library ? `/admin/libraries/${encodeURIComponent(library.id)}/raw` : null
  const { data: rawData, refetch: refetchRaw } = useRawEntityQuery<Record<string, unknown>>(
    rawEndpoint,
    isOpen && !!rawEndpoint
  )
  const rawSaveMutation = useSaveRawEntityMutation<Record<string, unknown>>({
    endpoint: rawEndpoint ?? "",
    invalidateOnSuccess: [["admin", "libraries"]],
  })
  const rawStored = useMemo(() => rawData?.entity ?? {}, [rawData])
  const rawHash = rawData?.hash ?? ""
  const [rawResetSignal, setRawResetSignal] = useState(0)

  useEffect(() => {
    setRawResetSignal((n) => n + 1)
  }, [rawStored])

  const handleRawSave = async (next: unknown, reason: string) => {
    if (!library || !rawEndpoint || !rawHash) return
    const parsed = (next || {}) as Record<string, unknown>
    try {
      await rawSaveMutation.mutateAsync({
        entity: parsed,
        expectedHash: rawHash,
        reason,
        source: "raw",
      })
      void refetchRaw()
      toast.success(
        t("adminDashboard.libraryEdit.toastSavedRaw", { defaultValue: "Library saved (raw)" })
      )
      onOpenChange(false)
    } catch (err) {
      toast.error(
        t("adminDashboard.libraryEdit.toastRawSaveFailed", { defaultValue: "Raw save failed" }),
        { description: err instanceof Error ? err.message : undefined }
      )
      throw err
    }
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!library || !editor.validate()) return
    await onSubmit(library.id, editor.values)
    onOpenChange(false)
  }

  if (!library) return null

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className={`flex max-h-[90vh] flex-col gap-6 overflow-hidden ${library.search_backend === "filth" ? "sm:max-w-4xl" : "sm:max-w-[600px]"}`}
      >
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="size-5" />
            {t("adminDashboard.libraries.editDialog.title", "Edit Library")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "adminDashboard.libraries.editDialog.description",
              "Update the library configuration. Changes will take effect on the next indexing run."
            )}
          </DialogDescription>
        </DialogHeader>
        <Tabs className="flex min-h-0 flex-1 flex-col" defaultValue="form">
          <TabsList>
            <TabsTrigger value="form">
              {t("adminDashboard.libraryEdit.tabForm", { defaultValue: "Form" })}
            </TabsTrigger>
            {library.search_backend === "filth" && (
              <TabsTrigger value="search">
                {t("adminDashboard.filthSettings.searchOverrides")}
              </TabsTrigger>
            )}
            <TabsTrigger
              className="text-destructive data-[state=active]:text-destructive"
              value="raw"
            >
              {t("adminDashboard.libraryEdit.tabRaw", { defaultValue: "Raw JSON" })}
            </TabsTrigger>
          </TabsList>
          {library.search_backend === "filth" && (
            <TabsContent className="min-h-0 overflow-y-auto" value="search">
              <FilthSearchSettings key={library.id} library id={library.id} />
            </TabsContent>
          )}
          <TabsContent className="mt-4 flex min-h-0 flex-1 flex-col" value="form">
            <form className="flex min-h-0 flex-1 flex-col space-y-4" onSubmit={handleSubmit}>
              <div className="flex-1 overflow-y-auto pr-2">
                <LibraryEditorForm
                  active={isOpen}
                  editor={editor}
                  embeddingModels={embeddingModels}
                />
              </div>
              <DialogFooter className="flex-shrink-0">
                <Button
                  disabled={isPending}
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                >
                  {t("adminDashboard.libraries.editDialog.cancel", "Cancel")}
                </Button>
                <Button disabled={isPending} type="submit">
                  {isPending ? (
                    <>
                      <Spinner />
                      {t("adminDashboard.libraries.editDialog.saving", "Saving...")}
                    </>
                  ) : (
                    t("adminDashboard.libraries.editDialog.save", "Save Changes")
                  )}
                </Button>
              </DialogFooter>
            </form>
          </TabsContent>
          <TabsContent className="mt-4 max-h-[70vh] overflow-y-auto" value="raw">
            <RawJsonTab
              confirmToken={library.id}
              isPending={rawSaveMutation.isPending || !rawHash}
              resetSignal={rawResetSignal}
              value={rawStored}
              resourceLabel={t("adminDashboard.libraryEdit.rawResourceLabel", {
                defaultValue: "library {{id}}",
                id: library.id,
              })}
              onSave={handleRawSave}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
